import { useMemo } from 'react'
import { HugeiconsIcon } from '@hugeicons/react'
import { Tick01Icon, ArrowLeft01Icon, ArrowRight02Icon, Loading03Icon, UserIcon, UserGroupIcon } from '@hugeicons/core-free-icons'
import { toast } from 'sonner'
import { SurveyQuestion } from './SurveyQuestion'
import type { SurveyAssessment } from '../types/assessment.types'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Stepper,
  StepperIndicator,
  StepperItem,
  StepperNav,
  StepperSeparator,
  StepperTrigger,
} from '@/components/ui/stepper'

interface SurveyFormProps {
  assessment: SurveyAssessment
  currentPage: number
  answers: Record<string, number>
  isEmployee: boolean
  organizationName: string
  isSubmitting: boolean
  onPageChange: (page: number) => void
  onAnswerChange: (questionId: string, value: number) => void
  onSubmit: () => void
}

export function SurveyForm({
  assessment,
  currentPage,
  answers,
  isEmployee,
  organizationName,
  isSubmitting,
  onPageChange,
  onAnswerChange,
  onSubmit,
}: SurveyFormProps) {
  // Memoize derived values
  const questions = useMemo(
    () => assessment.assessmentType?.questions ?? [],
    [assessment.assessmentType?.questions],
  )

  const assessmentName = useMemo(
    () => assessment.assessmentType?.name ?? 'Assessment',
    [assessment.assessmentType?.name],
  )

  // Memoize question grouping (expensive operation)
  const questionGroups = useMemo(() => {
    const groups = new Map<string, typeof questions>()
    for (const question of questions) {
      const key = `${question.name}|${question.subName}`
      const existing = groups.get(key)
      if (existing) {
        existing.push(question)
      } else {
        groups.set(key, [question])
      }
    }
    return groups
  }, [questions])

  const groupKeys = useMemo(
    () => Array.from(questionGroups.keys()),
    [questionGroups],
  )
  const totalPages = groupKeys.length

  // Memoize steps for stepper
  const steps = useMemo(() => {
    return groupKeys.map((key, index) => {
      const [categoryName = '', subCategoryName = ''] = key.split('|')
      return {
        number: index + 1,
        title: subCategoryName ? `${subCategoryName}` : categoryName,
        fullTitle: subCategoryName
          ? `${categoryName} / ${subCategoryName}`
          : categoryName,
      }
    })
  }, [groupKeys])

  // Memoize current page data
  const currentPageData = useMemo(() => {
    const currentGroupKey = groupKeys[currentPage - 1]
    const currentQuestions = currentGroupKey
      ? (questionGroups.get(currentGroupKey) ?? [])
      : []
    const [categoryName = '', subCategoryName = ''] = currentGroupKey
      ? currentGroupKey.split('|')
      : []

    const questionStartNumber =
      groupKeys
        .slice(0, currentPage - 1)
        .reduce((sum, key) => sum + (questionGroups.get(key)?.length ?? 0), 0) +
      1

    return {
      currentQuestions,
      categoryName,
      subCategoryName,
      questionStartNumber,
    }
  }, [groupKeys, currentPage, questionGroups])

  // Memoize progress calculations
  const progressData = useMemo(() => {
    const answeredCount = Object.keys(answers).length
    const progressPercentage = (answeredCount / questions.length) * 100
    const currentPageAnswered = currentPageData.currentQuestions.every(
      (q) => q.id in answers,
    )
    const allQuestionsAnswered = answeredCount === questions.length

    // Calculate completed steps
    const completedSteps = new Set<number>()
    for (let i = 0; i < groupKeys.length; i++) {
      const groupKey = groupKeys[i]
      const groupQuestions = questionGroups.get(groupKey) ?? []
      const allAnswered = groupQuestions.every((q) => q.id in answers)
      if (allAnswered) {
        completedSteps.add(i + 1) // steps are 1-indexed
      }
    }

    return {
      answeredCount,
      progressPercentage,
      currentPageAnswered,
      allQuestionsAnswered,
      completedSteps,
    }
  }, [
    answers,
    questions.length,
    currentPageData.currentQuestions,
    groupKeys,
    questionGroups,
  ])

  // Simple handlers that delegate to props  // Simple handlers that delegate to props
  const handlePrevious = () => {
    if (currentPage > 0) {
      onPageChange(currentPage - 1)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const handleSubmit = () => {
    if (!progressData.allQuestionsAnswered) {
      toast.error('Please answer all questions before submitting', {
        description: `${progressData.answeredCount} of ${questions.length} questions answered`,
      })
      return
    }

    if (isSubmitting) {
      return // Prevent double submission
    }

    onSubmit()
  }

  const handleStepperChange = (step: number) => {
    // Only allow navigation to completed steps or current step
    if (step === currentPage || progressData.completedSteps.has(step)) {
      onPageChange(step)
    }
  }

  // Render intro page (page 0)
  if (currentPage === 0) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-6">
          <div className="flex flex-col">
            <div className="flex items-center justify-between gap-4">
              <div className="text-2xl font-semibold">{assessmentName}</div>
              <Badge
                variant={isEmployee ? 'default' : 'secondary'}
                className="shrink-0"
              >
                {isEmployee ? (
                  <>
                    <HugeiconsIcon icon={UserIcon} className="mr-1 h-3 w-3" />
                    DirectReport
                  </>
                ) : (
                  <>
                    <HugeiconsIcon icon={UserGroupIcon} className="mr-1 h-3 w-3" />
                    Manager
                  </>
                )}
              </Badge>
            </div>
          </div>

          {/* Page Indicator */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              Page 0 of {totalPages}
            </span>
          </div>
        </div>

        {/* Intro Card */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-card rounded-lg border p-8">
            <h2 className="text-xl font-semibold mb-4">Intro</h2>
            <div className="space-y-4 text-muted-foreground">
              <p>
                {isEmployee
                  ? 'This survey is designed to gather feedback on your performance and contributions.'
                  : `This survey is designed to gather feedback on ${assessment.employee?.name ?? 'your employee'}'s performance and contributions.`}
              </p>
              <p>
                Please answer the questions honestly and to the best of your
                ability.
              </p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-end">
            <Button onClick={handleNext}>
              Start
              <HugeiconsIcon icon={ArrowRight02Icon} className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <div className="flex flex-col gap-6">
        <div className="flex flex-col">
          <div className="flex items-center justify-between gap-4">
            <div className="text-2xl font-semibold">{assessmentName}</div>
            <Badge
              variant={isEmployee ? 'default' : 'secondary'}
              className="shrink-0"
            >
              {isEmployee ? (
                <>
                  <HugeiconsIcon icon={UserIcon} className="mr-1 h-3 w-3" />
                  DirectReport
                </>
              ) : (
                <>
                  <HugeiconsIcon icon={UserGroupIcon} className="mr-1 h-3 w-3" />
                  Manager
                </>
              )}
            </Badge>
          </div>
        </div>
        <div className="space-y-6">
          {/* Stepper - only show when not on intro page */}
          {currentPage > 0 && (
            <Stepper
              value={currentPage}
              onValueChange={handleStepperChange}
              indicators={{
                completed: <HugeiconsIcon icon={Tick01Icon} className="size-3.5" />,
                loading: <HugeiconsIcon icon={Loading03Icon} className="size-3.5 animate-spin" />,
              }}
              className="mb-2"
            >
              <StepperNav className="mb-2">
                {steps.map((step, index) => {
                  const stepNumber = step.number
                  const isCompleted =
                    progressData.completedSteps.has(stepNumber)
                  const isInactive = stepNumber > currentPage
                  const isLastStep = index === steps.length - 1

                  return (
                    <StepperItem
                      key={stepNumber}
                      step={stepNumber}
                      completed={isCompleted}
                      disabled={isInactive && !isCompleted}
                    >
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <StepperTrigger>
                            <StepperIndicator className="size-5 border-2 data-[state=completed]:bg-primary data-[state=completed]:border-primary data-[state=completed]:text-white data-[state=active]:text-primary-foreground data-[state=active]:bg-primary data-[state=active]:border-primary data-[state=inactive]:border-muted">
                              <span className="hidden size-1.5 rounded-full bg-primary-foreground group-data-[state=active]/step:block"></span>
                            </StepperIndicator>
                          </StepperTrigger>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{step.fullTitle}</p>
                        </TooltipContent>
                      </Tooltip>
                      {!isLastStep && (
                        <StepperSeparator className="group-data-[state=completed]/step:bg-primary" />
                      )}
                    </StepperItem>
                  )
                })}
              </StepperNav>
            </Stepper>
          )}

          {/* Page Indicator */}
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground text-sm">
              Section {currentPage} of {totalPages}
            </span>

            <span className="text-muted-foreground text-sm">
              {currentPageData.currentQuestions.length} question
              {currentPageData.currentQuestions.length !== 1 ? 's' : ''} in this
              section
            </span>
          </div>
        </div>
      </div>

      {/* Questions */}
      <div className="space-y-3 max-w-4xl mx-auto">
        {/* Section Header */}

        {currentPageData.categoryName && (
          <div className="text-lg font-semibold mb-4">
            {currentPageData.categoryName}
            {currentPageData.subCategoryName &&
              ` / ${currentPageData.subCategoryName}`}
          </div>
        )}

        {currentPageData.currentQuestions.map((question, index) => (
          <SurveyQuestion
            key={question.id}
            question={question}
            questionNumber={currentPageData.questionStartNumber + index}
            value={answers[question.id]}
            onChange={onAnswerChange}
            isEmployee={isEmployee}
            employee={assessment.employee ?? null}
            organizationName={organizationName}
          />
        ))}
      </div>

      {/* Navigation Buttons */}
      <div className="max-w-4xl mx-auto">
        <div>
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={currentPage === 0}
            >
              <HugeiconsIcon icon={ArrowLeft01Icon} className="mr-2 h-4 w-4" />
              Previous
            </Button>

            <div className="text-sm text-muted-foreground">
              {progressData.currentPageAnswered ? (
                <span className="text-primary font-medium">
                  <HugeiconsIcon icon={Tick01Icon} className="inline mr-1 h-4 w-4" /> Page complete
                </span>
              ) : (
                <span className="hidden md:inline">
                  Answer all questions to continue
                </span>
              )}
            </div>

            {currentPage < totalPages ? (
              <Button
                onClick={handleNext}
                disabled={!progressData.currentPageAnswered}
              >
                Next
                <HugeiconsIcon icon={ArrowRight02Icon} className="ml-2 h-4 w-4" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={!progressData.allQuestionsAnswered || isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
